import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Header, Icon, Label, Loader, Message, Modal } from 'semantic-ui-react';
import ClientContractHubPanel from '../contracts/ClientContractHubPanel';
import ClientObservationsPanel from './ClientObservationsPanel';
import {
  contractService,
  ClientContractHubResponseDto,
} from '../../services/contracts/ContractService';
import { ClientHubPosVendaProtocoloDto } from '../../types/posVenda';
import { readHubProtocolos } from '../../utils/posVenda/posVendaHubUtils';
import AbrirProtocoloPosVendaLink from '../posVenda/AbrirProtocoloPosVendaLink';
import ClientHubProtocolosTable from '../posVenda/ClientHubProtocolosTable';
import { clientService } from '../../services/clients/ClientService';
import { cleanCnpj, formatCnpj } from '../../services/utils/stringUtils';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import {
  CLIENT_CLASSIFICATION_OPTIONS,
  buildClientUpdatePayloadFromResponse,
  getClientClassificationMeta,
  normalizeClientClassificationCode,
  readClientClassification,
  readClientInternalNotes,
} from '../../utils/clientClassification';

interface ClientContractHistoryModalProps {
  open: boolean;
  clientId?: string;
  cnpj?: string;
  razaoSocial?: string;
  scrollTo?: 'observacoes';
  onClientMetadataUpdated?: (payload: {
    clientId: string;
    clientClassification: string;
    internalNotes: string;
  }) => void;
  onClose: () => void;
}

const CLASSIFICATION_CSS_COLORS: Record<string, string> = {
  red: '#e74c3c',
  yellow: '#e67e22',
  blue: '#2980b9',
  green: '#27ae60',
};

const SECTION_HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)',
  borderRadius: '10px 10px 0 0',
  borderBottom: '2px solid #dee2e6',
  margin: '0 0 0 0',
};

const SECTION_BODY_STYLE: React.CSSProperties = {
  background: 'white',
  borderRadius: '0 0 10px 10px',
  border: '1px solid #dee2e6',
  borderTop: 'none',
  padding: '16px',
};

const SECTION_WRAPPER_STYLE: React.CSSProperties = {
  marginBottom: 20,
  borderRadius: 10,
  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
  overflow: 'hidden',
};

export const ClientContractHistoryModal: React.FC<ClientContractHistoryModalProps> = ({
  open,
  clientId,
  cnpj,
  razaoSocial,
  scrollTo,
  onClientMetadataUpdated,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hub, setHub] = useState<ClientContractHubResponseDto | null>(null);
  const [protocolosPosVenda, setProtocolosPosVenda] = useState<ClientHubPosVendaProtocoloDto[]>([]);
  const [isEditingClassification, setIsEditingClassification] = useState(false);
  const [savingClientMetadata, setSavingClientMetadata] = useState(false);
  const [clientClassification, setClientClassification] = useState('CLIENTE_REGULAR');
  const observacoesSectionRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const isAdmin = user?.roles?.includes('Admin');
  const canManageObservations = isAdmin || user?.roles?.includes('TeamLeader');

  const syncMetadataFromHub = useCallback((hubData: ClientContractHubResponseDto) => {
    if (!hubData.client) return;
    setClientClassification(normalizeClientClassificationCode(readClientClassification(hubData.client)));
    setIsEditingClassification(false);
  }, []);

  const loadHub = useCallback(async () => {
    if (!clientId && !cnpj && !razaoSocial) return;
    try {
      setLoading(true);
      setError(null);
      const data = await contractService.getClientContractHub({
        clientId,
        cnpj: cnpj ? cleanCnpj(cnpj) : undefined,
        razaoSocial: razaoSocial?.trim() || undefined,
        timelineLimit: 300,
      });
      setHub(data);
      syncMetadataFromHub(data);
      setProtocolosPosVenda(readHubProtocolos(data));
    } catch (err: unknown) {
      const apiMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        apiMessage || (err instanceof Error ? err.message : 'Erro ao carregar histórico.')
      );
      setHub(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, cnpj, razaoSocial, syncMetadataFromHub]);

  useEffect(() => {
    if (open) void loadHub();
    else {
      setHub(null);
      setProtocolosPosVenda([]);
      setError(null);
      setIsEditingClassification(false);
    }
  }, [open, loadHub]);

  useEffect(() => {
    if (!open || !hub?.client || scrollTo !== 'observacoes') return;
    const timer = window.setTimeout(() => {
      observacoesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [open, hub, scrollTo]);

  const persistClientMetadata = async (overrides: {
    clientClassification?: string;
    internalNotes?: string;
  }) => {
    if (!clientId) {
      showError('Cliente não identificado para salvar dados internos.');
      return;
    }
    try {
      setSavingClientMetadata(true);
      const fullClient = await clientService.getClientById(clientId);
      const payload = {
        ...buildClientUpdatePayloadFromResponse(fullClient),
        ...overrides,
      };
      await clientService.updateClient(clientId, payload);
      showSuccess('Dados internos do cliente atualizados.');
      setIsEditingClassification(false);
      await loadHub();
      onClientMetadataUpdated?.({
        clientId,
        clientClassification: normalizeClientClassificationCode(
          overrides.clientClassification ?? clientClassification
        ),
        internalNotes: readClientInternalNotes(fullClient) ?? '',
      });
    } catch (err: unknown) {
      const apiMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showError(
        apiMessage || (err instanceof Error ? err.message : 'Erro ao atualizar dados internos do cliente.')
      );
    } finally {
      setSavingClientMetadata(false);
    }
  };

  const handleSaveClassification = async () => {
    await persistClientMetadata({
      clientClassification: normalizeClientClassificationCode(clientClassification),
    });
  };

  const classificationMeta = getClientClassificationMeta(clientClassification);
  const classificationColor = CLASSIFICATION_CSS_COLORS[classificationMeta.color] ?? '#2980b9';

  const clientData = hub?.client;
  const displayName = clientData?.razaoSocial ?? razaoSocial ?? 'Cliente';
  const displayCnpj = clientData?.cnpj ? formatCnpj(clientData.cnpj) : (cnpj ?? '');
  const displayLocation = [clientData?.cidade, clientData?.estado].filter(Boolean).join('/');

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="large"
      closeOnDimmerClick
      style={{
        borderRadius: 20,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        overflow: 'hidden',
      }}
    >
      {/* ── HEADER ── */}
      <Modal.Header
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          color: 'white',
          padding: '20px 24px 16px',
          border: 'none',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Elemento decorativo circular */}
        <div
          style={{
            position: 'absolute',
            top: '-60px',
            right: '-40px',
            width: 200,
            height: 200,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-40px',
            right: '15%',
            width: 120,
            height: 120,
            background: 'rgba(255,255,255,0.04)',
            borderRadius: '50%',
            pointerEvents: 'none',
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
          {/* Lado esquerdo: info do cliente */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Linha 1: ícone + título */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name="building" style={{ margin: 0, color: 'white' }} />
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 1 }}>
                  Histórico do cliente
                </div>
                <Header
                  as="h2"
                  style={{
                    color: 'white',
                    margin: 0,
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '500px',
                  }}
                  title={displayName}
                >
                  {displayName}
                </Header>
              </div>
            </div>

            {/* Linha 2: metadados + badge de classificação */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginLeft: 44, marginTop: 4 }}>
              {displayCnpj && (
                <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4, lineHeight: 1 }}>
                  <Icon name="id card outline" style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1, verticalAlign: 'middle' }} />
                  {displayCnpj}
                </span>
              )}
              {displayLocation && (
                <>
                  <span style={{ color: 'rgba(255,255,255,0.35)', lineHeight: 1, display: 'flex', alignItems: 'center' }}>·</span>
                  <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4, lineHeight: 1 }}>
                    <Icon name="map marker alternate" style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1, verticalAlign: 'middle' }} />
                    {displayLocation}
                  </span>
                </>
              )}
              <span style={{ color: 'rgba(255,255,255,0.35)', lineHeight: 1, display: 'flex', alignItems: 'center' }}>·</span>
              <span
                style={{
                  background: classificationColor,
                  color: 'white',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  padding: '3px 10px',
                  borderRadius: 20,
                  letterSpacing: 0.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  lineHeight: 1,
                  boxShadow: `0 2px 8px ${classificationColor}55`,
                }}
              >
                <Icon name="tag" style={{ margin: 0, fontSize: '0.72rem', lineHeight: 1, verticalAlign: 'middle' }} />
                {classificationMeta.label}
              </span>
            </div>
          </div>

          {/* Lado direito: botão atualizar */}
          <Button
            size="mini"
            onClick={() => void loadHub()}
            loading={loading}
            style={{
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 20,
              padding: '6px 14px',
              marginLeft: 16,
              flexShrink: 0,
              alignSelf: 'center',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="refresh" style={{ margin: 0, lineHeight: 1, verticalAlign: 'middle' }} />
            Atualizar
          </Button>
        </div>
      </Modal.Header>

      {/* ── CONTENT ── */}
      <Modal.Content
        scrolling
        style={{
          maxHeight: '78vh',
          background: '#f4f6f9',
          padding: '20px 22px',
        }}
      >
        {error && (
          <Message negative style={{ borderRadius: 10, marginBottom: 16 }}>
            <Icon name="exclamation triangle" />
            {error}
          </Message>
        )}

        {/* Loader inicial */}
        {loading && !hub && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Loader active inline="centered" content="Carregando histórico..." />
          </div>
        )}

        {hub?.client && (
          <>
            {/* ── SEÇÃO: CLASSIFICAÇÃO ── */}
            <div style={SECTION_WRAPPER_STYLE}>
              <div style={{ ...SECTION_HEADER_STYLE, borderLeft: `4px solid ${classificationColor}` }}>
                <Icon name="tag" style={{ color: classificationColor, margin: 0 }} />
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                  Classificação do Cliente
                </span>
                {isAdmin && !isEditingClassification && (
                  <Button
                    size="mini"
                    basic
                    icon="edit"
                    content="Editar"
                    style={{ marginLeft: 'auto', borderRadius: 20 }}
                    onClick={() => setIsEditingClassification(true)}
                  />
                )}
              </div>
              <div style={SECTION_BODY_STYLE}>
                {!isEditingClassification ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Label color={classificationMeta.color} size="large" style={{ borderRadius: 20 }}>
                      <Icon name="tag" />
                      {classificationMeta.label}
                    </Label>
                    {!isAdmin && (
                      <span style={{ color: '#888', fontSize: '0.8rem' }}>
                        Somente administradores podem alterar a classificação.
                      </span>
                    )}
                  </div>
                ) : (
                  <Form size="small">
                    <Form.Field style={{ maxWidth: 360 }}>
                      <label style={{ fontWeight: 600, color: '#2c3e50', marginBottom: 6, display: 'block' }}>
                        Nova classificação
                      </label>
                      <select
                        value={clientClassification}
                        onChange={(e) => setClientClassification(e.target.value)}
                        style={{
                          width: '100%',
                          height: 40,
                          borderRadius: 8,
                          border: '1px solid #ced4da',
                          padding: '0 12px',
                          fontSize: '0.9rem',
                          background: 'white',
                          color: '#2c3e50',
                        }}
                      >
                        {CLIENT_CLASSIFICATION_OPTIONS.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Form.Field>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <Button
                        color="green"
                        size="small"
                        loading={savingClientMetadata}
                        disabled={savingClientMetadata}
                        onClick={() => void handleSaveClassification()}
                        style={{ borderRadius: 20 }}
                      >
                        <Icon name="check" />
                        Salvar
                      </Button>
                      <Button
                        size="small"
                        disabled={savingClientMetadata}
                        style={{ borderRadius: 20 }}
                        onClick={() => {
                          setIsEditingClassification(false);
                          setClientClassification(
                            normalizeClientClassificationCode(readClientClassification(hub.client))
                          );
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </Form>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── SEÇÃO: PÓS-VENDA ── */}
        {!loading && hub?.client && (
          <div style={SECTION_WRAPPER_STYLE}>
            <div style={{ ...SECTION_HEADER_STYLE, borderLeft: '4px solid #16a085' }}>
              <Icon name="headphones" style={{ color: '#16a085', margin: 0 }} />
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                Atendimento pós-venda
              </span>
              {protocolosPosVenda.length > 0 && (
                <span
                  style={{
                    background: '#16a085',
                    color: 'white',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    padding: '2px 9px',
                    borderRadius: 20,
                    marginLeft: 6,
                  }}
                >
                  {protocolosPosVenda.length}
                </span>
              )}
            </div>
            <div style={SECTION_BODY_STYLE}>
              <div style={{ marginBottom: 12 }}>
                <AbrirProtocoloPosVendaLink clientId={hub.client.id} />
              </div>
              <ClientHubProtocolosTable
                clientId={hub.client.id}
                protocolos={protocolosPosVenda}
                onBeforeNavigate={onClose}
              />
            </div>
          </div>
        )}

        {/* ── SEÇÃO: CONTRATOS ── */}
        {!loading && hub && (hub.contracts?.length ?? 0) > 0 && (
          <div style={SECTION_WRAPPER_STYLE}>
            <div style={{ ...SECTION_HEADER_STYLE, borderLeft: '4px solid #2980b9' }}>
              <Icon name="file text outline" style={{ color: '#2980b9', margin: 0 }} />
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                Contratos e Histórico
              </span>
              <span
                style={{
                  background: '#2980b9',
                  color: 'white',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  padding: '2px 9px',
                  borderRadius: 20,
                  marginLeft: 6,
                }}
              >
                {hub.contracts.length}
              </span>
            </div>
            <div style={{ ...SECTION_BODY_STYLE, padding: '12px 8px' }}>
              <ClientContractHubPanel
                key={hub.client?.id ?? 'hub'}
                hub={hub}
                showClientHeader={false}
              />
            </div>
          </div>
        )}

        {!loading && hub && (hub.contracts?.length ?? 0) === 0 && (
          <Message warning style={{ borderRadius: 10, marginBottom: 16 }}>
            <Icon name="warning sign" />
            Nenhum contrato encontrado para este cliente.
          </Message>
        )}

        {/* ── SEÇÃO: OBSERVAÇÕES INTERNAS ── */}
        {hub?.client && clientId && (
          <div
            id="client-internal-notes-section"
            ref={observacoesSectionRef}
            style={SECTION_WRAPPER_STYLE}
          >
            <div style={{ ...SECTION_HEADER_STYLE, borderLeft: '4px solid #8e44ad' }}>
              <Icon name="sticky note outline" style={{ color: '#8e44ad', margin: 0 }} />
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                Observações Internas
              </span>
              {(hub.observations?.length ?? 0) > 0 && (
                <span
                  style={{
                    background: '#8e44ad',
                    color: 'white',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    padding: '2px 9px',
                    borderRadius: 20,
                    marginLeft: 6,
                  }}
                >
                  {hub.observations?.length}
                </span>
              )}
            </div>

            <div style={SECTION_BODY_STYLE}>
              <ClientObservationsPanel
                clientId={clientId}
                observations={hub.observations ?? []}
                canAdd={canManageObservations}
                sourceType="client_history"
                onObservationsChanged={loadHub}
              />
            </div>
          </div>
        )}
      </Modal.Content>

      {/* ── ACTIONS ── */}
      <Modal.Actions
        style={{
          background: '#f4f6f9',
          borderTop: '1px solid #dee2e6',
          padding: '14px 22px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          style={{
            borderRadius: 20,
            background: '#2c3e50',
            color: 'white',
            border: 'none',
            padding: '8px 24px',
            fontWeight: 600,
          }}
          onClick={onClose}
        >
          <Icon name="times" />
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ClientContractHistoryModal;
